import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { 
  BookOpen, Search, LogIn, Loader2, BookMarked, UserPlus, 
  User, Calendar, Tag, Grid3X3, LayoutGrid, List, Images,
  TrendingUp, Clock, History, Heart, Key, LogOut, ChevronDown, X, Star
} from 'lucide-react';
import axios from 'axios';
import { withEditorRef, reloadIfSuspended } from '../lib/editorRef';
import { useCatalogNews, NewBadge, CatalogNewsBanner } from './CatalogNews';
import { BookDetailsModal, CatalogPagination } from './BookDetailsModal';
import { MobileBookCard, MobileListRow, CoverOnlyCard } from './MobileBookCard';

const NARROW_QUERY = '(max-width: 640px)';

const PAGE_SIZE = 12;
import { ReadingHistoryModal } from './ReadingHistoryModal';
import { CatalogFooter } from './CatalogFooter';
import { FavoriteHeart, BookInfoButton } from './FavoriteHeart';
import { MySubscriptionModal } from './MySubscriptionModal';
import { BookStars } from './BookStars';
import { ContinueReading } from './ContinueReading';
import { CollectionsBar, bookCollections, collectionSlug, GERAL } from './Collections';
import { CreditCard } from 'lucide-react';
import { AuthorLinks, CategoryLink, FacetChip, matchesFacet } from './CatalogFacets';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isLightColor = (hex) => {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return (0.299 * r + 0.587 * g + 0.114 * b) > 170;
};

const edAno = (b) => (b.edicao ? `${b.edicao}${b.ano_publicacao ? ` (${b.ano_publicacao})` : ''}` : (b.ano_publicacao || '-'));

const DigitalCatalog = ({ editorId, editorSlug }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [books, setBooks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [facet, setFacet] = useState(null);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [subscriberInfo, setSubscriberInfo] = useState(null);
  const [valorAssinatura, setValorAssinatura] = useState(0);
  const controlsRef = useRef(null);
  const [controlsH, setControlsH] = useState(74);
  useEffect(() => {
    const el = controlsRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setControlsH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  });
  const [viewMode, setViewMode] = useState('covers'); // 'covers' (só capas, padrão), 'grid3' (3), 'default' (2), 'list' (1)
  const [page, setPage] = useState(1);
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const sync = () => setIsNarrow(mq.matches);
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const [detailBook, setDetailBook] = useState(null);
  const [sortMode, setSortMode] = useState('relevance'); // 'relevance', 'recent', 'oldest'
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showMySub, setShowMySub] = useState(false);
  const [mySubTab, setMySubTab] = useState('card');
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get('renovar')) return;
    let tokenEditor = null;
    try { tokenEditor = JSON.parse(atob(localStorage.getItem('subscriberToken').split('.')[1])).editor_id; } catch (e) { /* not logged in */ }
    if (tokenEditor === editorId) { setMySubTab('card'); setShowMySub(true); navigate(window.location.pathname, { replace: true }); return; }
    navigate(`/${editorSlug}/login?redirect=${encodeURIComponent(`/${editorSlug}?renovar=1`)}`);
  }, [editorId, editorSlug]);
  const [myRatings, setMyRatings] = useState({});
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const userMenuRef = useRef(null);
  const [theme, setTheme] = useState({
    primary_color: '#68AAB0',
    secondary_color: '#1a1f2e',
    accent_color: '#48BB78',
    text_color: '#2D3748'
  });
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Load saved display preferences from localStorage
  useEffect(() => {
    const savedPrefs = localStorage.getItem(`catalog_prefs_v3_${editorId}`);
    if (savedPrefs) {
      try {
        const prefs = JSON.parse(savedPrefs);
        if (prefs.viewMode) setViewMode(prefs.viewMode);
        if (prefs.sortMode) setSortMode(prefs.sortMode);
      } catch (e) {
        console.log('Error loading preferences:', e);
      }
    }
    setPrefsLoaded(true);
  }, [editorId]);

  // Save display preferences when they change (only after initial load)
  useEffect(() => {
    if (editorId && prefsLoaded) {
      localStorage.setItem(`catalog_prefs_v3_${editorId}`, JSON.stringify({
        viewMode,
        sortMode
      }));
    }
  }, [viewMode, sortMode, editorId, prefsLoaded]);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const response = await axios.get(`${API}/digital/${editorId}/catalog`);
        setEditor(response.data.editor);
        setBooks(response.data.books || []);
        setValorAssinatura(response.data.valor_assinatura || 0);
        
        // Set theme from editor settings
        if (response.data.editor?.theme) {
          setTheme(response.data.editor.theme);
        }
        
        // Check if user is logged in as subscriber and decode token
        const token = localStorage.getItem('subscriberToken');
        if (token) {
          setIsSubscriber(true);
          try {
            // Decode JWT to get subscriber info (base64 decode the payload)
            const payloadBase64 = token.split('.')[1];
            const payload = JSON.parse(atob(payloadBase64));
            setSubscriberInfo({
              name: payload.name || '',
              email: payload.email || '',
              subscriber_id: payload.subscriber_id
            });
          } catch (e) {
            console.error('Error decoding token:', e);
          }
        }
      } catch (err) {
        if (reloadIfSuspended(err)) return;
        console.error('Error loading catalog:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCatalog();
  }, [editorId]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const favoriteIds = new Set(favorites.map((f) => f.book_id));
  const toggleFavorite = async (bookId) => {
    const token = localStorage.getItem('subscriberToken');
    if (!token) return;
    const isFav = favoriteIds.has(bookId);
    setFavorites((prev) => (isFav ? prev.filter((f) => f.book_id !== bookId) : [...prev, { id: `tmp-${bookId}`, book_id: bookId, book: books.find((b) => b.id === bookId) }]));
    try {
      const url = `${API}/digital/${editorId}/subscriber/favorites/${bookId}?auth=${token}`;
      await (isFav ? axios.delete(url) : axios.post(url));
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
    loadFavorites();
  };
  const heartFor = (book, size) => (isSubscriber
    ? <FavoriteHeart bookId={book.id} active={favoriteIds.has(book.id)} onToggle={toggleFavorite} size={size} /> : null);
  useEffect(() => { if (isSubscriber) loadFavorites(); }, [isSubscriber]); // load hearts state
  useEffect(() => {
    const token = localStorage.getItem('subscriberToken');
    if (isSubscriber && token) axios.get(`${API}/digital/subscriber/ratings?auth=${token}`).then((r) => setMyRatings(r.data)).catch(() => {});
  }, [isSubscriber]);
  const rateBook = async (bookId, rating) => {
    const token = localStorage.getItem('subscriberToken');
    try {
      const { data } = await axios.post(`${API}/digital/subscriber/ratings/${bookId}?auth=${token}`, { rating });
      setMyRatings((m) => ({ ...m, [bookId]: data.rating }));
      setBooks((bs) => bs.map((b) => (b.id === bookId ? { ...b, rating_avg: data.rating_avg, rating_count: data.rating_count } : b)));
      return true;
    } catch (err) {
      console.error('Error rating book:', err);
      return false;
    }
  };

  // Load favorites
  const loadFavorites = async () => {
    const token = localStorage.getItem('subscriberToken');
    if (!token) return;
    
    setLoadingFavorites(true);
    try {
      const response = await axios.get(`${API}/digital/${editorId}/subscriber/favorites?auth=${token}`);
      setFavorites(response.data.favorites || []);
    } catch (err) {
      console.error('Error loading favorites:', err);
    } finally {
      setLoadingFavorites(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    ['subscriberToken', 'subscriberData', 'readerToken', 'readerPlatformsCount'].forEach((k) => localStorage.removeItem(k));
    setIsSubscriber(false);
    setSubscriberInfo(null);
    setShowUserMenu(false);
  };

  // Handle change password
  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('As senhas não coincidem');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    const token = localStorage.getItem('subscriberToken');
    if (!token) return;
    
    try {
      await axios.post(`${API}/digital/${editorId}/subscriber/change-password?auth=${token}`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      setPasswordSuccess('Senha alterada com sucesso!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Erro ao alterar senha');
    }
  };

  // Get first name from full name
  const { newIds, markSeen } = useCatalogNews(isSubscriber);
  const [onlyNew, setOnlyNew] = useState(false);
  const [collection, setCollection] = useState(null);
  useEffect(() => { setPage(1); }, [searchTerm, sortMode, onlyNew]);
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('colecao');
    if (!wanted || !editor) return;
    const match = [GERAL, ...(editor.collections || [])].find((c) => collectionSlug(c.nome) === collectionSlug(wanted));
    if (match && books.some((b) => bookCollections(b, editor.collections || []).some((c) => c.id === match.id))) setCollection(match.id);
  }, [editor, books]);

  const getFirstName = (fullName) => {
    if (!fullName) return 'Usuário';
    return fullName.split(' ')[0];
  };

  // Filter and sort books
  const filteredBooks = books
    .filter((book) => !onlyNew || newIds.has(book.id))
    .filter((book) => !collection || bookCollections(book, editor?.collections || []).some((c) => c.id === collection))
    .filter((book) => matchesFacet(book, facet))
    .filter(book => 
      book.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.autor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.categoria?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.isbn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (searchTerm.replace(/[^0-9xX]/g, '').length >= 3 &&
        book.isbn?.replace(/[^0-9xX]/g, '').toLowerCase().includes(searchTerm.replace(/[^0-9xX]/g, '').toLowerCase()))
    )
    .sort((a, b) => {
      switch (sortMode) {
        case 'recent':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'rating':
          return (b.rating_avg || 0) - (a.rating_avg || 0) || (b.rating_count || 0) - (a.rating_count || 0) || (a.titulo || '').localeCompare(b.titulo || '', 'pt-BR');
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'relevance':
        default:
          // Sort by access count (most accessed first), fallback to title
          return (b.access_count || 0) - (a.access_count || 0);
      }
    });

  const pageSize = viewMode === 'covers' ? (isNarrow ? 12 : 24) : PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(filteredBooks.length / pageSize));
  const currentPageNum = Math.min(page, totalPages);
  const pagedBooks = filteredBooks.slice((currentPageNum - 1) * pageSize, currentPageNum * pageSize);
  const goToPage = (n) => { setPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const applyFacet = (f) => { setDetailBook(null); setFacet(f); setPage(1); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const goHome = () => {
    setSearchTerm(''); setFacet(null); setOnlyNew(false); setSortMode('relevance'); setPage(1); setDetailBook(null);
    if (window.location.search) navigate(`/${editorSlug}`, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const authorLinks = (book) => <AuthorLinks book={book} onFacet={applyFacet} color={theme.primary_color} />;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const handleReadBook = (bookId) => {
    if (!isSubscriber) {
      navigate(`/${editorSlug}/login?redirect=/${editorSlug}/livro/${bookId}`);
      return;
    }
    navigate(`/${editorSlug}/livro/${bookId}`);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        background: theme.secondary_color
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: theme.primary_color }} />
          <p>Carregando catálogo...</p>
        </div>
      </div>
    );
  }

  if (!editor) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        background: theme.secondary_color,
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: theme.primary_color }} />
          <h2 style={{ marginBottom: '1rem' }}>Biblioteca não encontrada</h2>
          <Button onClick={() => navigate('/')} style={{ background: theme.primary_color }}>
            Voltar para o início
          </Button>
        </div>
      </div>
    );
  }

  const mobileGuest = isNarrow && !isSubscriber;
  return (
    <div style={{ minHeight: '100vh', background: '#1a2332', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, background: theme.page_bg_color || '#f7fafc' }} data-testid="catalog-page-body">
      {/* Header: solid editor color, text contrast adapts */}
      <header style={{ 
        background: theme.secondary_color,
        color: isLightColor(theme.secondary_color) ? '#1a202c' : 'white',
        padding: '1.5rem 0',
        paddingBottom: isNarrow ? '1.5rem' : `calc(1.5rem + ${Math.round(controlsH / 2)}px)`
      }} data-testid="catalog-header">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isNarrow ? '0 1rem' : '0 2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: mobileGuest ? 'stretch' : 'center', flexDirection: mobileGuest ? 'column-reverse' : 'row', gap: mobileGuest ? '1rem' : 0 }}>
            {/* Header Image (280x100) or Title/Description */}
            <div style={{ flex: 1, minWidth: 0, textAlign: isNarrow ? 'center' : 'left' }} data-testid="catalog-header-logo-wrap">
              <a href={`/${editorSlug}`} onClick={(e) => { e.preventDefault(); goHome(); }} title="Voltar ao início do catálogo" data-testid="catalog-home-link"
                style={{ cursor: 'pointer', color: 'inherit', display: 'inline-block', textDecoration: 'none' }}>
              {editor.header_image_url ? (
                <img 
                  src={editor.header_image_url.startsWith('/') ? `${BACKEND_URL}${editor.header_image_url}` : editor.header_image_url} 
                  alt={editor.nome}
                  style={{ 
                    width: '280px', 
                    maxWidth: '100%',
                    height: '100px', 
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                  data-testid="editor-header-image"
                />
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <BookMarked className="w-8 h-8" style={{ color: theme.primary_color }} />
                    <h1 style={{ fontSize: '1.75rem', fontWeight: 700 }}>{editor.nome}</h1>
                  </div>
                  {editor.descricao ? (
                    <p style={{ color: 'inherit', opacity: 0.75 }}>{editor.descricao}</p>
                  ) : editor.instituicao && (
                    <p style={{ color: 'inherit', opacity: 0.75 }}>{editor.instituicao}</p>
                  )}
                </div>
              )}
              </a>
            </div>
            
            {/* User Actions */}
            <div style={{ textAlign: 'right', position: 'relative', ...(mobileGuest ? { width: '100%' } : {}) }} ref={userMenuRef}>
              {isSubscriber ? (
                <>
                  <button 
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      border: `1px solid ${theme.primary_color}`,
                      background: 'transparent',
                      color: theme.primary_color,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    data-testid="user-menu-btn"
                  >
                    <User className="w-5 h-5" />
                    <ChevronDown className="w-4 h-4" style={{ 
                      transform: showUserMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showUserMenu && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '0.5rem',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                      minWidth: '260px',
                      zIndex: 1000,
                      overflow: 'hidden'
                    }}>
                      {/* User Info Header */}
                      <div style={{
                        padding: '1rem',
                        background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.primary_color}cc 100%)`,
                        color: 'white'
                      }}>
                        <p style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.25rem' }}>
                          {getFirstName(subscriberInfo?.name)}
                        </p>
                        <p style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                          {subscriberInfo?.email}
                        </p>
                      </div>
                      
                      {/* Menu Options */}
                      <div style={{ padding: '0.5rem 0' }}>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowFavorites(true);
                            loadFavorites();
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            width: '100%',
                            padding: '0.75rem 1rem',
                            border: 'none',
                            background: 'transparent',
                            color: '#4a5568',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '0.95rem',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.background = '#f7fafc'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="favorites-btn"
                        >
                          <Heart className="w-5 h-5" style={{ color: '#e53e3e' }} />
                          Meus Favoritos
                        </button>
                        
                        <button
                          onClick={() => { setShowUserMenu(false); setShowHistory(true); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.75rem 1rem', border: 'none', background: 'transparent', color: '#4a5568', cursor: 'pointer', textAlign: 'left', fontSize: '0.95rem', transition: 'background 0.2s ease' }}
                          onMouseOver={(e) => e.currentTarget.style.background = '#f7fafc'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="history-btn"
                        >
                          <History className="w-5 h-5" style={{ color: '#3182ce' }} />
                          Histórico
                        </button>

                        <button
                          onClick={() => { setShowUserMenu(false); setShowMySub(true); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.75rem 1rem', border: 'none', background: 'transparent', color: '#4a5568', cursor: 'pointer', textAlign: 'left', fontSize: '0.95rem', transition: 'background 0.2s ease' }}
                          onMouseOver={(e) => e.currentTarget.style.background = '#f7fafc'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="my-subscription-btn"
                        >
                          <CreditCard className="w-5 h-5" style={{ color: '#38a169' }} />
                          Minha assinatura
                        </button>
                        
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowChangePassword(true);
                            setPasswordError('');
                            setPasswordSuccess('');
                            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            width: '100%',
                            padding: '0.75rem 1rem',
                            border: 'none',
                            background: 'transparent',
                            color: '#4a5568',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '0.95rem',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.background = '#f7fafc'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="change-password-btn"
                        >
                          <Key className="w-5 h-5" style={{ color: '#805ad5' }} />
                          Alterar Senha
                        </button>
                        
                        {Number(localStorage.getItem('readerPlatformsCount') || 0) > 1 && (
                          <button
                            onClick={() => navigate(`/${editorSlug}/login?step=select`)}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.75rem 1rem', border: 'none', background: 'transparent', color: '#4a5568', cursor: 'pointer', textAlign: 'left', fontSize: '0.95rem' }}
                            onMouseOver={(e) => e.currentTarget.style.background = '#f7fafc'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                            data-testid="switch-platform-btn"
                          >
                            <BookOpen className="w-5 h-5" style={{ color: '#68AAB0' }} />
                            Trocar plataforma
                          </button>
                        )}
                        
                        <div style={{ height: '1px', background: '#e2e8f0', margin: '0.5rem 0' }} />
                        
                        <button
                          onClick={handleLogout}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            width: '100%',
                            padding: '0.75rem 1rem',
                            border: 'none',
                            background: 'transparent',
                            color: '#e53e3e',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '0.95rem',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.background = '#fff5f5'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="logout-btn"
                        >
                          <LogOut className="w-5 h-5" />
                          Sair
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'flex-end', flexWrap: mobileGuest ? 'nowrap' : 'wrap' }} data-testid="catalog-guest-actions">
                  <Button
                    onClick={() => navigate(editor.modo_teste ? `/${editorSlug}/modo-teste` : `/${editorSlug}/login?action=subscribe`)}
                    style={{ background: theme.accent_color || '#F28C28', color: 'white', fontWeight: 600, ...(isNarrow ? { flex: 1, height: 44, padding: '0 0.5rem', fontSize: '0.85rem', whiteSpace: 'nowrap' } : {}) }}
                    title={editor.plataforma_publica ? 'Cadastre-se grátis nesta biblioteca' : 'Assine esta biblioteca'}
                    aria-label={editor.plataforma_publica ? 'Cadastre-se grátis nesta biblioteca' : 'Assine esta biblioteca'}
                    data-testid="subscribe-header-btn"
                  >
                    {isNarrow ? (editor.plataforma_publica ? <><UserPlus className="w-4 h-4 mr-2" />Cadastre-se</>
                      : valorAssinatura ? `Assinar (${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorAssinatura)}/mês)` : 'Assinar') : editor.plataforma_publica
                      ? 'Cadastre-se grátis nesta biblioteca'
                      : valorAssinatura
                        ? `Assine esta biblioteca por ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorAssinatura)}/mês`
                        : 'Assine esta biblioteca'}
                  </Button>
                  <Button 
                    onClick={() => navigate(`/${editorSlug}/login`)}
                    style={{ background: theme.primary_color, ...(isNarrow ? { flex: 1, height: 44 } : {}) }}
                    title={editor.plataforma_publica ? 'Entrar como Leitor' : 'Entrar como Assinante'}
                    aria-label={editor.plataforma_publica ? 'Entrar como Leitor' : 'Entrar como Assinante'}
                    data-testid="login-btn"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    {isNarrow ? 'Entrar' : (editor.plataforma_publica ? 'Entrar como Leitor' : 'Entrar como Assinante')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Search & Books */}
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: isNarrow ? '1rem' : '0 2rem 2rem' }}>
        {/* Search & Controls */}
        <div ref={controlsRef} data-testid="catalog-controls-bar" style={{ position: 'relative', zIndex: 2, marginTop: isNarrow ? 0 : -Math.round(controlsH / 2), marginBottom: isNarrow ? '1rem' : '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: isNarrow ? 'wrap' : 'nowrap', gap: '1rem', padding: '0.85rem 1.1rem', borderRadius: 12, background: 'white', border: `1px solid ${theme.primary_color}55`, borderLeft: `4px solid ${theme.primary_color}`, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          {/* Search with Book Count */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isNarrow ? '0.6rem' : '1rem', width: isNarrow ? '100%' : 'auto', minWidth: 0, flex: isNarrow ? undefined : '1 1 auto' }}>
            <div style={{ position: 'relative', width: '100%', maxWidth: isNarrow ? '100%' : '364px', minWidth: isNarrow ? 0 : '180px', flex: 1 }}>
              <Search className="w-5 h-5" style={{ 
                position: 'absolute', 
                left: '0.75rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#a0aec0'
              }} />
              <Input
                placeholder="Buscar por título, autor, categoria ou ISBN/ISSN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '2.5rem' }}
                data-testid="search-input"
              />
            </div>
            <span style={{ 
              fontSize: '0.9rem', 
              color: '#718096',
              whiteSpace: 'nowrap',
              fontWeight: 500
            }} data-testid="catalog-results-count">
              {(searchTerm.trim() || onlyNew || facet || collection) ? `${filteredBooks.length} de ` : ''}{books.length} {books.length === 1 ? 'livro' : 'livros'}
            </span>
          </div>
          
          {/* View & Sort Controls */}
          <div style={{ display: 'flex', gap: isNarrow ? '0.4rem' : '1rem', alignItems: 'center', flexWrap: 'nowrap', width: isNarrow ? '100%' : 'auto', minWidth: 0, flexShrink: 0 }} data-testid="catalog-view-sort-row">
            {/* View Mode Buttons */}
            <div className="catalog-view-group" style={{ display: 'flex', gap: '0.25rem', background: '#f1f5f9', borderRadius: '8px', padding: '4px', flexShrink: 0 }}>
              <button
                onClick={() => setViewMode('grid3')}
                style={{
                  padding: isNarrow ? '0.4rem' : '0.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'grid3' ? theme.primary_color : 'transparent',
                  color: viewMode === 'grid3' ? 'white' : '#64748b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="3 por linha"
                data-testid="view-mode-grid3"
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('default')}
                style={{
                  padding: isNarrow ? '0.4rem' : '0.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'default' ? theme.primary_color : 'transparent',
                  color: viewMode === 'default' ? 'white' : '#64748b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="2 por linha"
                data-testid="view-mode-grid"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: isNarrow ? '0.4rem' : '0.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'list' ? theme.primary_color : 'transparent',
                  color: viewMode === 'list' ? 'white' : '#64748b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Lista"
                data-testid="view-mode-list"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('covers')}
                style={{
                  padding: isNarrow ? '0.4rem' : '0.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'covers' ? theme.primary_color : 'transparent',
                  color: viewMode === 'covers' ? 'white' : '#64748b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={isNarrow ? 'Só capas (2 por linha)' : 'Só capas (6 por linha)'}
                data-testid="view-mode-covers"
              >
                <Images className="w-4 h-4" />
              </button>
            </div>
            
            {/* Sort Mode Buttons - Text labels */}
            {isNarrow && <span data-testid="catalog-sort-label" style={{ fontSize: '0.66rem', color: '#64748b', whiteSpace: 'nowrap', marginLeft: 'auto' }}>Ordenar:</span>}
            <div className="catalog-sort-group" style={{ display: 'flex', gap: '0.25rem', background: '#f1f5f9', borderRadius: '8px', padding: '4px', flexShrink: 0 }}>
              <button
                onClick={() => setSortMode('relevance')}
                title="Mais acessados"
                aria-label="Mais acessados"
                data-testid="sort-relevance-btn"
                style={{
                  padding: isNarrow ? '0.5rem 0.55rem' : '0.5rem 0.75rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: sortMode === 'relevance' ? theme.primary_color : 'transparent',
                  color: sortMode === 'relevance' ? 'white' : '#64748b',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: sortMode === 'relevance' ? 500 : 400,
                  whiteSpace: 'nowrap'
                }}
              >
                {isNarrow ? <TrendingUp className="w-4 h-4" /> : <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}><TrendingUp className="w-3.5 h-3.5" />Mais acessados</span>}
              </button>
              <button
                onClick={() => setSortMode('recent')}
                title="Mais recentes"
                aria-label="Mais recentes"
                data-testid="sort-recent-btn"
                style={{
                  padding: isNarrow ? '0.5rem 0.55rem' : '0.5rem 0.75rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: sortMode === 'recent' ? theme.primary_color : 'transparent',
                  color: sortMode === 'recent' ? 'white' : '#64748b',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: sortMode === 'recent' ? 500 : 400,
                  whiteSpace: 'nowrap'
                }}
              >
                {isNarrow ? <Clock className="w-4 h-4" /> : <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}><Clock className="w-3.5 h-3.5" />Mais recentes</span>}
              </button>
              <button
                onClick={() => setSortMode('oldest')}
                title="Mais antigos"
                aria-label="Mais antigos"
                data-testid="sort-oldest-btn"
                style={{
                  padding: isNarrow ? '0.5rem 0.55rem' : '0.5rem 0.75rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: sortMode === 'oldest' ? theme.primary_color : 'transparent',
                  color: sortMode === 'oldest' ? 'white' : '#64748b',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: sortMode === 'oldest' ? 500 : 400,
                  whiteSpace: 'nowrap'
                }}
              >
                {isNarrow ? <History className="w-4 h-4" /> : <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}><History className="w-3.5 h-3.5" />Mais antigos</span>}
              </button>
              <button
                onClick={() => setSortMode('rating')}
                title="Melhor avaliados"
                aria-label="Melhor avaliados"
                data-testid="sort-rating-btn"
                style={{ padding: isNarrow ? '0.5rem 0.55rem' : '0.5rem 0.75rem', borderRadius: '6px', border: 'none', background: sortMode === 'rating' ? theme.primary_color : 'transparent',
                  color: sortMode === 'rating' ? 'white' : '#64748b', cursor: 'pointer', fontSize: '0.85rem', fontWeight: sortMode === 'rating' ? 500 : 400, whiteSpace: 'nowrap' }}
              >
                {isNarrow ? <Star className="w-4 h-4" /> : <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}><Star className="w-3.5 h-3.5" />Melhor avaliados</span>}
              </button>
            </div>
          </div>
        </div>

        <CatalogNewsBanner
          count={newIds.size}
          onlyNew={onlyNew}
          onToggleOnlyNew={() => setOnlyNew((v) => !v)}
          onMarkSeen={() => { setOnlyNew(false); markSeen(); }}
          color={theme.primary_color}
        />

        <CollectionsBar collections={editor?.collections || []} books={books} selected={collection} onSelect={(c) => { setCollection(c); setPage(1); }} color={theme.primary_color} />

        {facet && <div className="catalog-block-gap"><FacetChip facet={facet} onClear={() => { setFacet(null); setPage(1); }} color={theme.primary_color} /></div>}

        {/* Books Grid */}
        {filteredBooks.length === 0 ? (
          <Card>
            <CardContent style={{ padding: '3rem', textAlign: 'center', color: '#718096' }}>
              <BookOpen className="w-12 h-12 mx-auto mb-2" style={{ opacity: 0.5 }} />
              <p>{searchTerm ? 'Nenhum livro encontrado' : 'Nenhum livro publicado ainda'}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Header row for list view */}
            {viewMode === 'list' && !isNarrow && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '0.75rem 0',
                marginBottom: '0.5rem',
                borderBottom: '2px solid #e2e8f0'
              }}>
                <div style={{ width: '50px', minWidth: '50px' }}></div>
                <div style={{ 
                  flex: 1, 
                  display: 'grid', 
                  gridTemplateColumns: '2fr 1.5fr 0.8fr 1fr 1fr',
                  gap: '1rem',
                  padding: '0 1rem'
                }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Título</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Autor(es)</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Edição/Ano</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Editora</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ISBN/ISSN</span>
                </div>
                <div style={{ width: '50px', minWidth: '50px' }}></div>
              </div>
            )}
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: viewMode === 'covers' ? (isNarrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(6, minmax(0, 1fr))') : viewMode === 'list' ? 'minmax(0, 1fr)' : viewMode === 'grid3' ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))', 
              gap: viewMode === 'covers' ? (isNarrow ? '1rem 0.75rem' : '1.5rem 1rem') : viewMode === 'list' ? '0.5rem' : (isNarrow ? '0.6rem' : '1.5rem')
            }}>
            {pagedBooks.map((book) => {
              if (viewMode === 'covers') {
                return <CoverOnlyCard key={book.id} book={book} color={theme.primary_color} accent={theme.accent_color || '#F28C28'} backend={BACKEND_URL}
                  onRead={() => handleReadBook(book.id)} onDetails={() => setDetailBook(book)} />;
              }
              if (isNarrow) {
                const Row = viewMode === 'list' ? MobileListRow : MobileBookCard;
                return (
                  <Row key={book.id} book={book} color={theme.primary_color} backend={BACKEND_URL} isNew={newIds.has(book.id)}
                    compact={viewMode === 'grid3'} onRead={() => handleReadBook(book.id)} onDetails={() => setDetailBook(book)}
                    heart={heartFor(book, viewMode === 'list' ? 20 : (viewMode === 'grid3' ? 14 : 16))}
                    stars={<BookStars book={book} myRating={myRatings[book.id]} onRate={isSubscriber ? rateBook : null} size={viewMode === 'grid3' ? 10 : 12} />} authors={viewMode === 'grid3' ? null : authorLinks(book)} />
                );
              }
              // List mode - horizontal single row with columns
              if (viewMode === 'list') {
                return (
                  <Card 
                    key={book.id} 
                    style={{ 
                      background: 'white',
                      overflow: 'hidden',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer',
                      border: '1px solid #e2e8f0',
                      position: 'relative'
                    }}
                    className="hover:shadow-lg"
                    data-testid={`book-card-${book.id}`}
                  >
                    {newIds.has(book.id) && <NewBadge color={theme.primary_color} />}
                    <div style={{ display: 'flex', alignItems: 'center', height: '70px' }}>
                      {/* Cover - 50x70px (half size) */}
                      <div onClick={() => handleReadBook(book.id)} data-testid={`book-cover-${book.id}`} style={{ 
                        width: '50px',
                        minWidth: '50px',
                        height: '70px',
                        background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.primary_color}99 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                      }}>
                        {book.capa_url ? (
                          <img 
                            src={book.capa_url.startsWith('http') ? book.capa_url : `${BACKEND_URL}${book.capa_url}`}
                            alt={book.titulo}
                            style={{ width: '50px', height: '70px', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div style={{ 
                          display: book.capa_url ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%'
                        }}>
                          <BookOpen className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.5)' }} />
                        </div>
                      </div>

                      {/* Columns - Title, Author, Year, Publisher, ISBN */}
                      <div onClick={() => setDetailBook(book)} data-testid={`book-info-${book.id}`} title="Ver dados completos" style={{ 
                        flex: 1, 
                        minWidth: 0,
                        display: 'grid', 
                        gridTemplateColumns: 'minmax(0,2fr) minmax(0,1.5fr) minmax(0,0.8fr) minmax(0,1fr) minmax(0,1fr)',
                        gap: '1rem',
                        padding: '0 1rem',
                        alignItems: 'center'
                      }}>
                        <p 
                          style={{ 
                            fontWeight: 600, 
                            fontSize: '0.9rem', 
                            color: theme.text_color,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={book.titulo}
                        >
                          {book.titulo}
                        </p>
                        <p 
                          style={{ 
                            fontSize: '0.85rem', 
                            color: '#4a5568',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={book.autor || '-'}
                        >
                          {authorLinks(book)}
                        </p>
                        <p 
                          style={{ 
                            fontSize: '0.85rem', 
                            color: '#4a5568',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={edAno(book)}
                        >
                          {edAno(book)}
                        </p>
                        <p 
                          style={{ 
                            fontSize: '0.85rem', 
                            color: '#4a5568',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={book.editora || (editor?.nome || editor?.bibliotecaNome) || '-'}
                        >
                          {book.editora || (editor?.nome || editor?.bibliotecaNome) || '-'}
                        </p>
                        <p 
                          style={{ 
                            fontSize: '0.85rem', 
                            color: '#4a5568',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={book.isbn || '-'}
                        >
                          {book.isbn || '-'}
                        </p>
                      </div>

                      {isSubscriber && <div style={{ padding: '0 0.5rem' }}>{heartFor(book, 20)}</div>}
                      {/* Read Button - icon only, editor theme color */}
                      <button
                        style={{ 
                          background: theme.primary_color,
                          color: 'white',
                          border: 'none',
                          width: '50px',
                          height: '70px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'filter 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReadBook(book.id);
                        }}
                        onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(0.88)'}
                        onMouseOut={(e) => e.currentTarget.style.filter = 'none'}
                        data-testid={`read-btn-${book.id}`}
                        title="Ler Agora"
                      >
                        <BookMarked className="w-5 h-5" />
                      </button>
                    </div>
                  </Card>
                );
              }
              
              // Grid modes (default 2 and grid3)
              const coverWidth = viewMode === 'grid3' ? 140 : 190;
              const coverHeight = viewMode === 'grid3' ? 200 : 300;
              const buttonHeight = viewMode === 'grid3' ? 40 : 50;
              const cardHeight = coverHeight + buttonHeight;
              const fontSize = viewMode === 'grid3' ? '0.875rem' : '0.9rem';
              const labelSize = viewMode === 'grid3' ? '0.65rem' : '0.75rem';
              const clean = viewMode === 'grid3';
              
              return (
              <Card 
                key={book.id} 
                style={{ borderTopLeftRadius: 0,  
                  background: 'white',
                  overflow: 'hidden',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer',
                  border: '1px solid #e2e8f0',
                  position: 'relative'
                }}
                className="hover:shadow-lg"
                data-testid={`book-card-${book.id}`}
              >
                {newIds.has(book.id) && <NewBadge color={theme.primary_color} />}
                <div style={{ display: 'flex', height: `${cardHeight}px` }}>
                  {/* Left Column - Cover + Button */}
                  <div style={{ 
                    width: `${coverWidth}px`,
                    minWidth: `${coverWidth}px`,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Book Cover */}
                    <div onClick={() => handleReadBook(book.id)} data-testid={`book-cover-${book.id}`} style={{ 
                      width: `${coverWidth}px`,
                      height: `${coverHeight}px`,
                      background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.primary_color}99 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}>
                      {book.capa_url ? (
                        <img 
                          src={book.capa_url.startsWith('http') ? book.capa_url : `${BACKEND_URL}${book.capa_url}`}
                          alt={book.titulo}
                          style={{ 
                            width: `${coverWidth}px`, 
                            height: `${coverHeight}px`, 
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div style={{ 
                        display: book.capa_url ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%'
                      }}>
                      <BookOpen className="w-12 h-12" style={{ color: 'rgba(255,255,255,0.5)' }} />
                      </div>
                    </div>
                    
                    {/* Read Button */}
                    <Button 
                      style={{ 
                        background: theme.primary_color,
                        color: 'white',
                        borderRadius: 0,
                        width: `${coverWidth}px`,
                        height: `${buttonHeight}px`,
                        minHeight: `${buttonHeight}px`,
                        fontSize: viewMode === 'grid3' ? '0.85rem' : '0.9rem'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReadBook(book.id);
                      }}
                      data-testid={`read-btn-${book.id}`}
                    >
                      <BookMarked className="w-4 h-4 mr-2" />
                      Ler Agora
                    </Button>
                  </div>

                  {/* Right Column - Book Info with 10px margins */}
                  <div onClick={() => setDetailBook(book)} data-testid={`book-info-${book.id}`} title="Ver dados completos" style={{ 
                    flex: 1, 
                    minWidth: 0,
                    overflow: 'hidden',
                    padding: '10px',
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'flex-start', 
                    paddingTop: clean ? '16px' : '8px',
                    paddingRight: isSubscriber ? '32px' : '10px',
                    paddingBottom: clean ? '38px' : '44px',
                    position: 'relative',
                    gap: clean ? '0.5rem' : '0.1rem',
                    borderLeft: '1px solid #e2e8f0'
                  }}>
                    {(clean || isSubscriber) && <div style={{ position: 'absolute', right: 6, bottom: 6, zIndex: 2, display: 'flex', alignItems: 'center' }}>
                      {clean && <BookInfoButton bookId={book.id} onOpen={() => setDetailBook(book)} size={20} color={theme.primary_color} />}
                      {heartFor(book, 20)}
                    </div>}
                    <div data-testid={`book-stars-row-${book.id}`} style={{ position: 'absolute', left: 0, right: (isSubscriber ? 34 : 0) + (clean ? 30 : 0), bottom: 0, padding: '8px 10px 9px', background: 'white', zIndex: 1 }}>
                      <BookStars book={book} myRating={myRatings[book.id]} onRate={isSubscriber ? rateBook : null} />
                    </div>
                    {/* Título */}
                    <div>
                      {!clean && <span style={{ fontSize: labelSize, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Título</span>}
                      <p 
                        style={{ 
                          fontSize: clean ? '1.175rem' : '1rem',
                          fontWeight: clean ? 700 : 600,
                          marginBottom: clean ? '0.15rem' : 0,
                          color: theme.text_color,
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          maxWidth: '100%',
                          ...(clean
                            ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', height: '2.6em', wordBreak: 'break-word' }
                            : { whiteSpace: 'nowrap', textOverflow: 'ellipsis' })
                        }}
                        title={book.titulo}
                        data-testid={`book-title-${book.id}`}
                      >
                        {book.titulo}
                      </p>
                    </div>
                    
                    {/* Autor(es) */}
                    <div>
                      {!clean && <span style={{ fontSize: labelSize, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Autor(es)</span>}
                      <p 
                        style={{ 
                          fontSize, 
                          color: '#4a5568',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%'
                        }}
                        title={book.autor || '-'}
                      >
                        {clean ? (book.autor || '-') : authorLinks(book)}
                      </p>
                    </div>
                    
                    {clean && <div data-testid={`book-divider-${book.id}`} style={{ height: 1, background: '#e2e8f0', width: '100%', flexShrink: 0 }} />}
                    {/* Edição/Ano */}
                    <div>
                      {!clean && <span style={{ fontSize: labelSize, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Edição/Ano</span>}
                      <p 
                        style={{ 
                          fontSize, 
                          color: '#4a5568',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%'
                        }}
                        title={edAno(book)}
                      >
                        {edAno(book)}
                      </p>
                    </div>
                    

                    {/* Editora */}
                    {!clean && <div>
                      {!clean && <span style={{ fontSize: labelSize, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Editora</span>}
                      <p 
                        style={{ 
                          fontSize, 
                          color: '#4a5568',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%'
                        }}
                        title={book.editora || (editor?.nome || editor?.bibliotecaNome) || '-'}
                      >
                        {book.editora || (editor?.nome || editor?.bibliotecaNome) || '-'}
                      </p>
                    </div>}
                    
                    {/* ISBN/ISSN */}
                    {!clean && <div>
                      {!clean && <span style={{ fontSize: labelSize, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ISBN/ISSN</span>}
                      <p 
                        style={{ 
                          fontSize, 
                          color: '#4a5568',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%'
                        }}
                        title={book.isbn || '-'}
                      >
                        {book.isbn || '-'}
                      </p>
                    </div>}

                    {/* Categoria */}
                    {!clean && <div>
                      {!clean && <span style={{ fontSize: labelSize, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Categoria</span>}
                      <p style={{ fontSize, color: '#4a5568', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}
                        title={book.categoria || '-'} data-testid={`book-categoria-${book.id}`}>
                        <CategoryLink book={book} onFacet={applyFacet} color={theme.primary_color} />
                      </p>
                    </div>}
                  </div>
                </div>
              </Card>
            );
            })}
          </div>
          <CatalogPagination page={currentPageNum} totalPages={totalPages} onChange={goToPage} color={theme.primary_color} />
          </>
        )}
      </main>
      {detailBook && (
        <BookDetailsModal collections={editor?.collections || []} book={{ ...detailBook, editora: detailBook.editora || (editor?.nome || editor?.bibliotecaNome) }} color={theme.primary_color} onClose={() => setDetailBook(null)} onFacet={applyFacet}
          onRead={() => { const id = detailBook.id; setDetailBook(null); handleReadBook(id); }} />
      )}

      {/* Favorites Modal */}
      {showHistory && (
        <ReadingHistoryModal
          editorId={editorId}
          onClose={() => setShowHistory(false)}
          onOpenBook={(id) => navigate(`/${editorSlug}/livro/${id}`)}
        />
      )}

      {showFavorites && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }} onClick={() => setShowFavorites(false)}>
          <div 
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '1.25rem',
              background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.primary_color}cc 100%)`,
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Heart className="w-6 h-6" />
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Meus Favoritos</h2>
              </div>
              <button
                onClick={() => setShowFavorites(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div style={{ padding: '1rem', maxHeight: '60vh', overflowY: 'auto' }}>
              {loadingFavorites ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: theme.primary_color }} />
                  <p style={{ color: '#718096', marginTop: '0.5rem' }}>Carregando favoritos...</p>
                </div>
              ) : favorites.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>
                  <Heart className="w-12 h-12 mx-auto mb-2" style={{ opacity: 0.3 }} />
                  <p>Você ainda não tem livros favoritos.</p>
                  <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                    Adicione livros aos favoritos durante a leitura!
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {favorites.map((fav) => (
                    <div 
                      key={fav.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        padding: '0.75rem',
                        background: '#f7fafc',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        border: '1px solid #e2e8f0'
                      }}
                      onClick={() => {
                        setShowFavorites(false);
                        handleReadBook(fav.book_id);
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#edf2f7';
                        e.currentTarget.style.borderColor = theme.primary_color;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = '#f7fafc';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      {/* Book Cover */}
                      <div style={{
                        width: '50px',
                        height: '70px',
                        background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.primary_color}99 100%)`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        flexShrink: 0
                      }}>
                        {fav.book?.capa_url ? (
                          <img 
                            src={fav.book.capa_url.startsWith('http') ? fav.book.capa_url : `${BACKEND_URL}${fav.book.capa_url}`}
                            alt={fav.book?.titulo}
                            style={{ width: '50px', height: '70px', objectFit: 'cover' }}
                          />
                        ) : (
                          <BookOpen className="w-6 h-6" style={{ color: 'rgba(255,255,255,0.5)' }} />
                        )}
                      </div>
                      
                      {/* Book Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ 
                          fontWeight: 600, 
                          color: theme.text_color,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {fav.book?.titulo}
                        </p>
                        <p style={{ 
                          fontSize: '0.85rem', 
                          color: '#718096',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {fav.book?.autor}
                        </p>
                      </div>
                      
                      {/* Read Button */}
                      <Button 
                        size="sm"
                        style={{ background: theme.primary_color, flexShrink: 0 }}
                      >
                        <BookMarked className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }} onClick={() => setShowChangePassword(false)}>
          <div 
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '400px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '1.25rem',
              background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.primary_color}cc 100%)`,
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Key className="w-6 h-6" />
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Alterar Senha</h2>
              </div>
              <button
                onClick={() => setShowChangePassword(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  color: '#4a5568'
                }}>
                  Senha atual
                </label>
                <Input
                  type="password"
                  placeholder="Digite sua senha atual"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                  data-testid="current-password-input"
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  color: '#4a5568'
                }}>
                  Nova Senha
                </label>
                <Input
                  type="password"
                  placeholder="Digite a nova senha"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  data-testid="new-password-input"
                />
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  color: '#4a5568'
                }}>
                  Confirmar Nova Senha
                </label>
                <Input
                  type="password"
                  placeholder="Confirme a nova senha"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  data-testid="confirm-password-input"
                />
              </div>
              
              {passwordError && (
                <div style={{
                  padding: '0.75rem',
                  background: '#fed7d7',
                  color: '#c53030',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  fontSize: '0.9rem'
                }}>
                  {passwordError}
                </div>
              )}
              
              {passwordSuccess && (
                <div style={{
                  padding: '0.75rem',
                  background: '#c6f6d5',
                  color: '#276749',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  fontSize: '0.9rem'
                }}>
                  {passwordSuccess}
                </div>
              )}
              
              <Button 
                onClick={handleChangePassword}
                style={{ 
                  width: '100%', 
                  background: theme.primary_color 
                }}
                data-testid="save-password-btn"
              >
                Salvar Nova Senha
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>

      {showMySub && <MySubscriptionModal onClose={() => setShowMySub(false)} color={theme.primary_color} initialTab={mySubTab} />}
      {isSubscriber && <ContinueReading editorId={editorId} color={theme.primary_color} onOpen={handleReadBook} visibleIds={new Set(books.map((b) => b.id))} />}
      <CatalogFooter isNarrow={isNarrow} primaryColor={theme.primary_color} onLogin={() => navigate(`/${editorSlug}/login`)} />
    </div>
  );
};

export default withEditorRef(DigitalCatalog);
